import { Box, CircularProgress, ThemeProvider } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import './App.scss';
import { DeveloperModuleService } from './app/admin/modules/service';
import { SettingsService } from './app/admin/settings/service';
import { AuthService } from './app/auth/service';
import { ERole } from './shared/enums/user/ERole';
import { ETheme } from './shared/enums/user/ETheme';
import { handleError } from './shared/functions/handleError';
import { ILayout } from './shared/interfaces/shared/ILayout';
import Layout from './shared/layout/Layout';
import {
  darkTheme,
  lightTheme,
  StyledMaterialDesignContent,
} from './shared/mui/theme';
import authSlice from './store/slices/auth.slice';
import companySettingsSlice from './store/slices/company-settings.slice';
import enumsSlice from './store/slices/enums.slice';
import moduleSlice from './store/slices/module.slice';
import { RootState } from './store/store';
import i18n from './utils/localization/i18n';
import { routeList } from './utils/routes';
import { publicRoutes } from './utils/routes/roles/publicRoutes';

function App() {
  const dispatch = useDispatch();
  const jwtToken = localStorage.getItem('token');
  const modules = useSelector((state: RootState) => state.module.list);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const { userData, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const company = useSelector((state: RootState) => state?.companySettings);
  const currentRoleRoutes = routeList?.find(
    (route) => route?.role === userData?.role,
  );
  const { filters } = useSelector((state: RootState) => state.module);
  const filteredRoutes = currentRoleRoutes?.routes?.filter(
    (route) =>
      userData?.role === ERole.DEVELOPER ||
      (route?.module?.length
        ? modules?.some(
            (module) =>
              module.isEnabled && route?.module === String(module?.name),
          )
        : true),
  );
  useEffect(() => {
    Promise.all([AuthService.enums(), SettingsService.getAll()])
      .then(async ([enumsResponse, companySettingsResponse]) => {
        dispatch(enumsSlice.actions.setEnums(enumsResponse));
        dispatch(
          companySettingsSlice.actions.setCompanySettings(
            companySettingsResponse,
          ),
        );
        localStorage.setItem(
          'primaryColor',
          companySettingsResponse?.primaryColor,
        );
        localStorage.setItem('companyName', companySettingsResponse?.title);
        localStorage.setItem(
          'faviconPath',
          companySettingsResponse?.faviconPath,
        );
      })
      .catch(handleError);
  }, [dispatch, jwtToken]);
  useEffect(() => {
    !!userData?.id &&
      DeveloperModuleService.getAll(filters)
        .then((modulesResponse) => {
          dispatch(moduleSlice.actions.setModules(modulesResponse));
        })
        .catch(handleError);
    // eslint-disable-next-line
  }, [dispatch, JSON.stringify(filters), userData?.id]);

  useEffect(() => {
    if (jwtToken) {
      setIsLoading(true);
      Promise.all([AuthService.me(jwtToken as string)])
        .then(async ([userResponse]) => {
          await i18n.changeLanguage(userResponse?.language);
          dispatch(
            authSlice.actions.setLoginState({
              userData: userResponse,
              token: jwtToken,
              isAuthenticated: true,
            }),
          );
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [dispatch, jwtToken]);

  useEffect(() => {
    if (userData?.theme) {
      setIsDarkTheme(userData?.theme === ETheme.DARK);
      if (userData?.theme === ETheme.LIGHT) {
        document.body.classList.remove('dark');
      } else {
        document.body.classList.add(userData?.theme);
      }
    }
  }, [userData?.theme]);

  useEffect(() => {
    if (company?.primaryColor) {
      document.documentElement.style.setProperty(
        '--primaryColor',
        company?.primaryColor,
      );
    }
  }, [company?.primaryColor]);

  if (isLoading || (!modules?.length && userData?.id)) {
    // Display loading spinner while checking authentication
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isDarkTheme
            ? darkTheme.palette.background.default
            : lightTheme.palette.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }
  return (
    <ThemeProvider
      theme={
        isDarkTheme
          ? {
              ...darkTheme,
              palette: {
                ...darkTheme.palette,
                primary: {
                  ...darkTheme.palette.primary,
                  main: company?.primaryColor,
                  dark: `${company?.primaryColor}0D`,
                },
              },
            }
          : {
              ...lightTheme,
              palette: {
                ...lightTheme.palette,
                primary: {
                  ...lightTheme.palette.primary,
                  main: company?.primaryColor,
                  dark: `${company?.primaryColor}0D`,
                },
              },
            }
      }
    >
      <SnackbarProvider
        maxSnack={3}
        Components={{
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: 'background.default', // Applies background color based on the current theme
            color: 'text.primary',
          }}
        >
          {isAuthenticated ? (
            <Layout routes={filteredRoutes as ILayout[]}>
              <Routes>
                {filteredRoutes?.map((route) => {
                  return (
                    <Route
                      path={route?.path}
                      element={route?.component}
                      key={route?.path}
                    />
                  );
                })}
              </Routes>
            </Layout>
          ) : (
            <Routes>
              {publicRoutes?.map((route) => {
                return (
                  <Route
                    path={route?.path}
                    element={route?.component}
                    key={route?.path}
                  />
                );
              })}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          )}
        </Box>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
